// Core React and third-party libraries
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// Redux and state management
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';

// UI Components and styling
import { getIcon, Loader, SuccessModal } from '@ohif/ui';
import { useTheme } from "../../ui/src/contextProviders";
import { servicesManager } from "../../app/src/App";

// Constants and utilities
import { _commonUrls, _machineUrls } from '@custom/Utils/constants/apiUrls';
import { _routes } from '@custom/Utils/constants/routes';
import { customStyle } from '@custom/Utils/utils';
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import editHospitalTeleModPrice from '@custom/rtk-apis/hospital/editHospitalTeleCredit'
import {clearHospitalEditTeleModPriceState} from "@custom/store/slices/hospital/editHospitalTeleCredit"
import { EditPricingProps } from '@custom/Utils/constants/interfaces';


// Define types for form values
interface ModalityPricingFormValues {
  modality: string;
  studyRegion: string;
  price: string | number;
}

const EditTeleradiologyPrices = ({ setShowEditForm, priceModalityData, setTableData, onUpdate, hospitalId }: EditPricingProps) => {
  // Theme context
  const isDarkMode = useTheme();
  // Redux hooks
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const modalityAndStudyRegion = useSelector((state: RootState) => state.getModalityStudySliceResponse);
  const editHospitalTeleModPriceSliceResponse = useSelector((state: RootState) => state.editHospitalTeleModPriceSliceResponse);

  // State management
  const [modal, setModal] = useState(false);
  const [message, setMessage] = useState('');
  const [modalities, setModalities] = useState([]);
  const [studyRegionList, setSetstudyRegionList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
   const { uiNotificationService } = servicesManager?.services;

  // Initial form values
  const initialValues: ModalityPricingFormValues = {
    modality: priceModalityData?.modality || '',
    studyRegion: priceModalityData?.studyRegion || '',
    price: priceModalityData?.credit || '', // Use 'credit' as per your Field value
  };

  // Form validation schema
  const validationSchema = Yup.object().shape({
    modality: Yup.string().required('Modality Name is required'),
    studyRegion: Yup.string().when('modality', {
      is: (modality) => modality !== 'Histopathology',
      then: (schema) => schema.required('Study Region is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    price: Yup.mixed()
      .test('is-valid-number', 'Modality Price must be a number', value => {
        return !isNaN(value) && value !== '' && value !== null;
      })
      .test('is-positive', 'Modality Price must be positive', value => {
        return parseFloat(value) > 0;
      })
      .required('Modality Price is required'),

  });

  /**
   * Handles form submission
   * @param formValues - The form values to be saved
   */
  const handlePriceUpdate = async (formValues: ModalityPricingFormValues) => {
  };

  const updateDataTable = (updatedItem) => {
    setTableData((prev) =>
      prev.map((item) =>
        item._id === priceModalityData._id ? updatedItem : item
      )
    );

    // Call onUpdate to propagate changes to parent
    if (onUpdate) {
      onUpdate(updatedItem);
    }
    setMessage('Modality pricing updated successfully');
    setModal(true);
    setTimeout(() => {
      setShowEditForm(false);
      setModal(false);
    }, 1700);
    setIsLoading(false)
  }

  const submitUpdatedPrice = (values, error, e) => {
    e.preventDefault(); // Prevent default form submission
    setIsLoading(true);
    if (Object.keys(error).length === 0) {
      let updatedItem = {
        _id: priceModalityData._id,
        modality: values.modality,
        studyRegion: values.studyRegion || '',
        credit: parseFloat(values.price), // Map price to credit
      };

      if (hospitalId) {
        updatedItem = {
          "hospitalId": hospitalId,
          "telecreditId": priceModalityData._id,
          "credit": parseFloat(values.price),
          "modality": values.modality,
          "studyRegion": values.studyRegion
        }
        dispatch(editHospitalTeleModPrice(updatedItem))
      } else {
        // Update tableData locally
        updateDataTable(updatedItem);
      }
    } else {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (editHospitalTeleModPriceSliceResponse.status === 'success' && editHospitalTeleModPriceSliceResponse.result) {
      // Update tableData locally
        const updatedItem = editHospitalTeleModPriceSliceResponse.result
       updateDataTable(updatedItem);
        dispatch(clearHospitalEditTeleModPriceState())
    }

    if(editHospitalTeleModPriceSliceResponse.status === 'ERROR' || editHospitalTeleModPriceSliceResponse.status === 'error' ){
       uiNotificationService.show({
          title: 'Update modality price',
          message: editHospitalTeleModPriceSliceResponse.msg || 'Something went wrong ....',
          type: 'error',
        })
        setIsLoading(false);
         dispatch(clearHospitalEditTeleModPriceState())
    }

  }, [editHospitalTeleModPriceSliceResponse.result, editHospitalTeleModPriceSliceResponse.status, editHospitalTeleModPriceSliceResponse.error]);


  useEffect(() => {
    if (modalityAndStudyRegion.status === 'SUCCESS' && modalityAndStudyRegion.result) {
      let fetchedModalities = modalityAndStudyRegion.result.modalities || [];
      if (priceModalityData?.modality === 'Histopathology' && !fetchedModalities.includes('Histopathology')) {
        fetchedModalities = [...fetchedModalities, 'Histopathology'];
      }
      setModalities(fetchedModalities);
      setSetstudyRegionList(modalityAndStudyRegion.result.studyRegions);
    }
  }, [modalityAndStudyRegion.result, modalityAndStudyRegion.status, modalityAndStudyRegion.error]);

  useEffect(() => {
    dispatch(getModalityStudy());
  }, []);

  return (
    <>
      <div
        className='page page__full'
        style={isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}
      >
        <div className='page__body'>
          <div className='details'>
            <div className='form form--innersec'>
              <div className="mb-3">
                <button
                  className=''
                  onClick={() => setShowEditForm(false)}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  {getIcon('icon-toolbar-back')}
                  Back
                </button>
              </div>
              <section className='form__section'>
                <h1 className='form__section__title'>Credit Details</h1>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handlePriceUpdate}
                >
                  {({ errors, values }) => (
                    <Form>
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label" htmlFor="modality">
                              Modality
                              <span className="required-asterisk">*</span>
                            </label>
                            <Field
                              as="select"
                              className="form__select form__select--sm"
                              style={customStyle.select}
                              name="modality"
                              disabled={true}
                            >
                              <option value="">-Select-</option>
                              {modalities?.map((modality) => (
                                <option key={modality} value={modality}>
                                  {modality}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage name="modality" component="div" className="error text-error" />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label" htmlFor="studyRegion">
                              Study Region
                              {values.modality !== 'Histopathology' && <span className="required-asterisk">*</span>}
                            </label>
                            <Field
                              as="select"
                              className="form__select form__select--sm"
                              style={customStyle.select}
                              name="studyRegion"
                              disabled={true}
                            >
                              <option value="">-Select-</option>
                              {studyRegionList?.map((region) => (
                                <option key={region} value={region}>
                                  {region}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage name="studyRegion" component="div" className="error text-error" />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label" htmlFor="price">
                              Credits
                              <span className="required-asterisk">*</span>
                            </label>
                            <Field
                              className="form__input form__input--sm"
                              type="number"
                              id="price"
                              name="price"
                              placeholder="Modality Price"
                              autoComplete="off"
                            />
                            <ErrorMessage name="price" component="div" className="error text-error" />
                          </div>
                        </div>
                      </div>
                      <div className="btn__group">
                        <button
                          type="button"
                          className="btn btn__white"
                          onClick={() => setShowEditForm(false)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn__primary"
                          type="submit"
                          // disabled={isSubmitting}
                          onClick={(e) => submitUpdatedPrice(values, errors, e)}
                        >
                          Update
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </section>
            </div>
          </div>
        </div>
        <SuccessModal
          isSuccessModalOpen={modal}
          title={message}
          handleCloseModal={() => setModal(false)}
        />
        <Loader isLoaderOpen={isLoading} />
      </div>
    </>
  );
};

export default EditTeleradiologyPrices;
